import React, { Fragment, Suspense, lazy, useCallback, useMemo } from 'react';
import { Redirect, Switch, Route, RouteComponentProps } from 'react-router-dom';
import _ from 'lodash';
import { AuthenticationManager } from 'core';
import { alertMessage } from 'components/AlertDialog';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import RouteWithPermission from 'components/RouteWithPermission/RouteWithPermission';
import { hasFuncs } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { ROUTE_PATH } from 'enum/RoutePath';
import { useFinalReportPageModel } from './FinalReportPageModel';
import { FinalReportType, TargetKPI } from 'core/finalReport/FinalReport';

export const FinalReportPage: React.FC<
  RouteComponentProps & {
    authenticationManager: AuthenticationManager;
  }
> = ({ match, location, authenticationManager }) => {
  const { loading, data, reportId, modalData, redirectPath } =
    useFinalReportPageModel(
      location.search,
      location.pathname,
      authenticationManager.logined
    );
  const searchParams = new URLSearchParams(location.search);
  const reportType =
    searchParams.get('reportType') === FinalReportType.OUTDOOR
      ? FinalReportType.OUTDOOR
      : FinalReportType.RTB;
  const basicProps = useMemo(
    () => ({
      reportId,
      advertiser: _.get(data, 'advertiser', ''),
      settings: _.get(data, 'settings', []),
      currency: _.get(data, 'currency', 'TWD'),
      createTime: _.get(data, 'create_time', ''),
      targetKPI: _.get(data, 'target_KPI', TargetKPI.IMPRESSION),
      overallPerformance: _.get(data, 'overall_performance', {}),
      overallReports: _.get(data, 'overall_reports', {})
    }),
    [data, reportId]
  );

  const renderModalPage = useCallback(() => {
    return (
      <Fragment>
        {modalData &&
          alertMessage(modalData.title, modalData.message, modalData.onConfirm)}
      </Fragment>
    );
  }, [modalData]);

  const renderPageContent = useCallback(() => {
    const TenmaxReportContent = lazy(
      () => import('./TenmaxReportContent/TenmaxReportContent')
    );
    const OutdoorReportContent = lazy(
      () => import('./OutdoorReportContent/OutdoorReportContent')
    );

    if (!data) {
      return <div />;
    }
    return (
      <>
        <Suspense fallback={<LoadingIndicator />}>
          {reportType === FinalReportType.OUTDOOR ? (
            <OutdoorReportContent reportId={reportId} data={data} />
          ) : (
            <TenmaxReportContent
              {...basicProps}
              overallValues={_.get(data, 'overall_values', {})}
              chartConfig={_.get(data, 'chart_config', [])}
              l1ObjectInfo={_.get(data, 'platform_info', [])}
            />
          )}
        </Suspense>
      </>
    );
  }, [basicProps, data, reportType, reportId]);

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Fragment>
      {renderModalPage()}
      {redirectPath && <Redirect to={redirectPath} />}
      {authenticationManager.logined && (
        <Switch>
          <Route path={`*/${ROUTE_PATH.ERROR403}`} render={renderModalPage} />
          <RouteWithPermission
            path={`${match.url}`}
            permissionAware={hasFuncs(Permission.ORDER_READ)}
            redirectPath={`${match.url}/${ROUTE_PATH.ERROR403}`}
            render={renderPageContent}
          />
        </Switch>
      )}
    </Fragment>
  );
};
